<template>
  <div>
    <h2 class="section-title">Video-Verwaltung</h2>
    <p class="section-description">
      Hier kannst du YouTube-Videos verwalten, die auf der Hauptseite angezeigt werden.
    </p>
    
    <!-- Formular zum Hinzufügen/Bearbeiten von Videos -->
    <div class="card" v-if="showForm">
      <h3>{{ isEditing ? 'Video bearbeiten' : 'Neues Video hinzufügen' }}</h3>
      <form @submit.prevent="handleSubmit" class="video-form">
        <div class="form-group">
          <label for="title">Titel</label>
          <input 
            type="text" 
            id="title" 
            v-model="formData.title" 
            required
            placeholder="Video-Titel"
            class="form-control"
          >
        </div>
        
        <div class="form-group">
          <label for="youtube_url">YouTube URL</label>
          <input 
            type="text" 
            id="youtube_url" 
            v-model="formData.youtube_url" 
            required
            placeholder="https://www.youtube.com/watch?v=..."
            class="form-control"
          >
        </div>
        
        <div class="form-group">
          <label for="description">Beschreibung</label>
          <textarea 
            id="description" 
            v-model="formData.description"
            placeholder="Video-Beschreibung"
            class="form-control"
            rows="3"
          ></textarea>
        </div>
        
        <div class="actions">
          <button type="submit" class="btn btn-primary">
            {{ isEditing ? 'Aktualisieren' : 'Hinzufügen' }}
          </button>
          <button 
            type="button" 
            @click="cancelEdit" 
            class="btn btn-secondary"
          >
            Abbrechen
          </button>
        </div>
      </form>
    </div>
    
    <!-- Action Bar und Liste der Videos -->
    <div class="toolbar">
      <button 
        @click="showForm = true" 
        class="btn btn-primary"
        :disabled="showForm"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M12 5v14M5 12h14"></path>
        </svg>
        Neues Video hinzufügen
      </button>
    </div>
    
    <div v-if="videos.length === 0" class="empty-state">
      <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18"></rect>
        <line x1="7" y1="2" x2="7" y2="22"></line>
        <line x1="17" y1="2" x2="17" y2="22"></line>
        <line x1="2" y1="12" x2="22" y2="12"></line>
        <line x1="2" y1="7" x2="7" y2="7"></line>
        <line x1="2" y1="17" x2="7" y2="17"></line>
        <line x1="17" y1="17" x2="22" y2="17"></line>
        <line x1="17" y1="7" x2="22" y2="7"></line>
      </svg>
      <p>Noch keine Videos vorhanden. Füge dein erstes Video hinzu!</p>
    </div>
    
    <div v-else class="videos-list">
      <div v-for="video in videos" :key="video.id" class="card video-item">
        <div class="video-preview">
          <iframe 
            :src="getEmbedUrl(video.youtube_url)" 
            frameborder="0" 
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
            allowfullscreen
          ></iframe>
        </div>
        <div class="video-info">
          <h4>{{ video.title }}</h4>
          <p>{{ video.description }}</p>
          <div class="video-actions">
            <button @click="editVideo(video)" class="action-btn edit">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
              </svg>
              Bearbeiten
            </button>
            <button @click="deleteVideo(video.id)" class="action-btn delete">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              </svg>
              Löschen
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'VideoManager',
  data() {
    return {
      videos: [],
      formData: {
        id: null,
        title: '',
        youtube_url: '',
        description: ''
      },
      isEditing: false,
      showForm: false
    }
  },
  methods: {
    async loadVideos() {
      try {
        const response = await fetch('/api/videos_simple.php', {
          credentials: 'include'
        })
        const data = await response.json()
        if (data.success) {
          this.videos = data.videos
        } else {
          console.error('Fehler beim Laden der Videos:', data.error)
        }
      } catch (error) {
        console.error('Fehler beim Laden der Videos:', error)
      }
    },
    
    getEmbedUrl(youtubeUrl) {
      const videoId = this.extractVideoId(youtubeUrl)
      return `https://www.youtube.com/embed/${videoId}`
    },
    
    extractVideoId(url) {
      const regex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/
      const match = url.match(regex)
      return match ? match[1] : null
    },
    
    async handleSubmit() {
      try {
        // Admin-Passwort zu den Daten hinzufügen
        const videoData = {
          ...this.formData,
          admin_password: 'homeward2023'  // Einfache Auth-Lösung
        }
        
        const response = await fetch('/api/videos_simple.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify(videoData)
        })
        
        const data = await response.json()
        
        if (data.success) {
          await this.loadVideos()
          this.resetForm()
        } else {
          console.error('Fehler beim Speichern des Videos:', data.error)
        }
      } catch (error) {
        console.error('Fehler beim Speichern des Videos:', error)
      }
    },
    
    editVideo(video) {
      this.formData = { ...video }
      this.isEditing = true
      this.showForm = true
    },
    
    async deleteVideo(id) {
      if (!confirm('Möchten Sie dieses Video wirklich löschen?')) {
        return
      }
      
      try {
        const response = await fetch('/api/videos_simple.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({
            action: 'delete',
            id: id,
            admin_password: 'homeward2023'
          })
        })
        
        const data = await response.json()
        
        if (data.success) {
          await this.loadVideos()
        } else {
          console.error('Fehler beim Löschen des Videos:', data.error)
        }
      } catch (error) {
        console.error('Fehler beim Löschen des Videos:', error)
      }
    },
    
    cancelEdit() {
      this.resetForm()
    },
    
    resetForm() {
      this.formData = {
        id: null,
        title: '',
        youtube_url: '',
        description: ''
      }
      this.isEditing = false
      this.showForm = false
    }
  },
  mounted() {
    this.loadVideos()
  }
}
</script>

<style scoped>
.section-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: var(--text-color);
}

.section-description {
  color: var(--text-muted);
  margin-bottom: 2rem;
}

.card {
  background-color: var(--white);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  padding: 1.5rem;
  margin-bottom: 1.5rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: var(--text-color);
}

.form-control {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 1rem;
  transition: all 0.2s ease;
}

.form-control:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(0, 132, 61, 0.1);
}

.actions {
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
}

.btn {
  padding: 0.75rem 1.25rem;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
  border: none;
}

.btn-primary {
  background-color: var(--primary-color);
  color: white;
}

.btn-primary:hover {
  background-color: var(--primary-dark);
}

.btn-secondary {
  background-color: #e9ecef;
  color: #212529;
}

.btn-secondary:hover {
  background-color: #dee2e6;
}

.toolbar {
  margin-bottom: 1.5rem;
  display: flex;
  justify-content: flex-start;
}

.empty-state {
  text-align: center;
  padding: 3rem;
  background-color: var(--white);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.empty-state svg {
  color: var(--text-muted);
  opacity: 0.5;
  margin-bottom: 1rem;
}

.empty-state p {
  color: var(--text-muted);
}

.videos-list {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.video-item {
  display: flex;
  gap: 1.5rem;
  padding: 1.5rem;
}

.video-preview {
  flex: 0 0 280px;
  position: relative;
  padding-bottom: 158px; /* 16:9 Aspect Ratio */
  height: 0;
  border-radius: 6px;
  overflow: hidden;
}

.video-preview iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.video-info {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.video-info h4 {
  margin: 0 0 0.5rem 0;
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--text-color);
}

.video-info p {
  margin: 0 0 1rem 0;
  color: var(--text-muted);
  line-height: 1.5;
}

.video-actions {
  margin-top: auto;
  display: flex;
  gap: 0.75rem;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  background: none;
  border: none;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.action-btn.edit {
  color: var(--primary-color);
}

.action-btn.edit:hover {
  background-color: rgba(0, 132, 61, 0.1);
}

.action-btn.delete {
  color: #e53935;
}

.action-btn.delete:hover {
  background-color: rgba(229, 57, 53, 0.1);
}

/* Responsive Anpassungen */
@media (max-width: 768px) {
  .video-item {
    flex-direction: column;
  }
  
  .video-preview {
    flex: none;
    width: 100%;
    padding-bottom: 56.25%;
  }
}
</style> 